import {gunzipSync, gzipSync, strFromU8, strToU8} from 'fflate';

import {getMd5Hash} from '@packages/utilities/md5-browser';

import type {SandboxFileSystem} from './actionCreators';

/**
 * Session-scoped cache of compiled code-library build artifacts, keyed by a
 * hash of the build inputs (EXT-297).
 *
 * Builds are whole-library: one `out.mjs` + `out.css` for every file in the
 * sandbox projection. Undo/redo restores a previously-built source state, so
 * keying the finished artifact by a content hash of the projected filesystem
 * lets the engine skip the multi-second Blaxel round trip (deps install →
 * typecheck → vite build → fs read) and re-dispatch the artifact it already
 * has.
 *
 * Artifacts are self-contained ESM bundles that inline the *development* build
 * of React/ReactDOM + the Tailwind runtime, so even a trivial component is
 * large — and every cached version stores a near-identical copy of that
 * framework code. To keep the cache memory-friendly we store entries
 * **gzip-compressed** (dev bundles are highly repetitive, so they compress
 * well), decompress on hit, and bound the cache by cumulative *compressed*
 * bytes. Compression is synchronous (`fflate`) so the cache API stays sync and
 * the engine's hit/miss path is unchanged; the ~tens-of-ms cost lands off the
 * interaction path (after a build for `set`, and far cheaper than a rebuild for
 * `get`).
 */

// `functionsHash` is stored uncompressed and excluded from the byte budget.
export type BuildArtifact = {js: string; css: string; functionsHash?: string};

export type BuildArtifactCache = {
  get(key: string): BuildArtifact | undefined;
  set(key: string, artifact: BuildArtifact): void;
  /** Introspection for tests / dev tooling — current entry count and total compressed bytes held. */
  stats(): {entries: number; bytes: number};
};

/**
 * Deterministic hash of the build inputs: every (path, source) pair in the
 * projected sandbox filesystem, sorted by path. Serialized via JSON.stringify
 * so path/source contents can't collide across pair boundaries. File `status`
 * is deliberately excluded — the same content must hash the same regardless of
 * sync state.
 */
export const computeBuildInputsHash = (
  filesystem: SandboxFileSystem
): string => {
  const sortedEntries = Object.entries(filesystem)
    .sort(([pathA], [pathB]) => (pathA < pathB ? -1 : pathA > pathB ? 1 : 0))
    .map(([path, file]) => [path, file.source]);
  return getMd5Hash(JSON.stringify(sortedEntries));
};

// A cache is not archival: favour low CPU on the (post-build / undo) path over
// squeezing out the last few percent of ratio. Level 4 still compresses the
// repetitive dev bundle heavily.
const GZIP_LEVEL = 4 as const;

const compress = (text: string): Uint8Array =>
  gzipSync(strToU8(text), {level: GZIP_LEVEL});

const decompress = (bytes: Uint8Array): string => strFromU8(gunzipSync(bytes));

type CompressedArtifact = {
  js: Uint8Array;
  css: Uint8Array;
  bytes: number;
  functionsHash?: string;
};

/**
 * Minimal LRU keyed by build-inputs hash, evicting by cumulative *compressed*
 * byte budget. `get` refreshes recency (Map insertion order is the recency
 * order) and decompresses; `set` compresses then evicts least-recently-used
 * entries until the budget holds. Artifacts whose compressed size exceeds the
 * whole budget are never cached.
 */
export const createBuildArtifactCache = (
  maxBytes: number
): BuildArtifactCache => {
  const entries = new Map<string, CompressedArtifact>();
  let totalBytes = 0;

  return {
    get(key) {
      const entry = entries.get(key);
      if (entry === undefined) return undefined;
      // Re-insert to mark as most recently used.
      entries.delete(key);
      entries.set(key, entry);
      return {
        js: decompress(entry.js),
        css: decompress(entry.css),
        functionsHash: entry.functionsHash,
      };
    },
    set(key, artifact) {
      const js = compress(artifact.js);
      const css = compress(artifact.css);
      const bytes = js.byteLength + css.byteLength;
      if (bytes > maxBytes) return;

      const existing = entries.get(key);
      if (existing !== undefined) {
        totalBytes -= existing.bytes;
        entries.delete(key);
      }
      entries.set(key, {js, css, bytes, functionsHash: artifact.functionsHash});
      totalBytes += bytes;

      while (totalBytes > maxBytes) {
        const oldestKey = entries.keys().next().value as string;
        const oldest = entries.get(oldestKey) as CompressedArtifact;
        totalBytes -= oldest.bytes;
        entries.delete(oldestKey);
      }
    },
    stats() {
      return {entries: entries.size, bytes: totalBytes};
    },
  };
};
